import React, { useState, useEffect, useContext, Suspense, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { useGLTF, OrbitControls } from '@react-three/drei';
import { DirectionalLight } from 'three';
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import CircularNicknameSvg from './CircularNicknameSvg';
import { getFirestore, doc, updateDoc, getDoc } from "firebase/firestore";
import { UserContext } from '../UserContext';
import { defaultBannerUrl, defaultAvatarUrl } from '../constants';
import { useParams } from "react-router-dom";
import { mapZodiacValueToImageUrl } from './valueToDisplayMappings.js';
import { Country, State } from 'country-state-city';
import YouTube from "react-youtube";
import axios from 'axios';
import PreviewMyFavoritesMovies from "../components/PreviewMyFavoritesMovies";
import MyFavoritesActors from "../components/MyFavoritesActors";
import My3FavoriteCategories from "../components/My3FavoriteCategories";
import UseWindowSize from '../components/UseWindowSize';
import { SRGBColorSpace, ACESFilmicToneMapping } from 'three'
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ChatWindow from '../components/ChatWindow';
import VideoCallManager from './VideoCallManager';
import UserXpAvatar from './UserXpAvatar';
import MovieReviewsCarousel from './MovieReviewsCarousel';
import { getUserActivityScore } from "./levelUtils";
import { useLanguage } from '../contexts/LanguageContext';


function MyComponent() {
    const [value, setValue] = useState('');

    return <ReactQuill theme="snow" value={value} onChange={setValue} />;
}

function ThreeModel() {
    return (
        <>
            <directionalLight intensity={3} position={[0, 10, 10]} />
            <Model />
        </>
    );
}



function Model() {
    const gltf = useGLTF('/images/cameraTech.glb');
    return <primitive object={gltf.scene} scale={[3, 3, 3]} />;
}


const EmuleProfil = ({ userId, onClose, selectedUser }) => {

    const { t } = useLanguage();
    const userContext = useContext(UserContext);
    const [userData, setUserData] = useState(selectedUser || {});
    const db = getFirestore();

    // Ajout d'une référence pour le composant YouTube
    const youtubePlayerRef = useRef(null);

    // États existants
    const [videoKey, setVideoKey] = useState("");
    const [favoritePreviews, setFavoritePreviews] = useState([]);
    const [favoriteActors, setFavoriteActors] = useState([]);
    const [favoriteCategories, setFavoriteCategories] = useState([]);
    const [isHovered, setIsHovered] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [zIndex, setZIndex] = useState(40000);
    const [isLoading, setIsLoading] = useState(true);
    const [imageChangeCounter, setImageChangeCounter] = useState(0);
    const { showAnimParam } = useParams();
    const [animationTriggered, setAnimationTriggered] = useState(false);
    const [showVideoCallModal, setShowVideoCallModal] = useState(false);

    // useEffect pour nettoyer le composant YouTube au démontage
    useEffect(() => {
        return () => {
            // Nettoyage du lecteur YouTube au démontage
            if (youtubePlayerRef.current && youtubePlayerRef.current.internalPlayer) {
                try {
                    youtubePlayerRef.current.internalPlayer.stopVideo();
                    youtubePlayerRef.current.internalPlayer.destroy();
                } catch (err) {
                    console.error("Erreur lors du nettoyage du lecteur YouTube:", err);
                }
            }
        };
    }, []);

    // useEffect pour gérer les ressources Three.js
    useEffect(() => {
        // Three.js crée automatiquement certaines ressources globales

        return () => {
            // Libération des ressources Three.js au démontage
            if (window.threeJSCanvasInstance) {
                try {
                    // Vider les ressources manuellement si nécessaire
                    // Dans la plupart des cas, React Three Fiber gère automatiquement le nettoyage
                    // mais il est toujours bon de s'assurer que tout est explicitement nettoyé
                    window.threeJSCanvasInstance = null;
                } catch (err) {
                    console.error("Erreur lors du nettoyage de Three.js:", err);
                }
            }
        };
    }, []);

    // Ajout d'un hook useEffect pour récupérer les données utilisateur si nécessaire
    useEffect(() => {
        const fetchUserData = async () => {
            console.log("EmuleProfil - début fetchUserData");
            console.log("EmuleProfil - selectedUser:", selectedUser);
            console.log("EmuleProfil - userId:", userId);

            if (selectedUser) {
                console.log("Utilisation des données utilisateur existantes pour EmuleProfil:", selectedUser);
                setUserData(selectedUser);
                setIsLoading(false);
                return;
            }

            if (!userId) {
                console.error("EmuleProfil: ID utilisateur non fourni");
                setErrorMsg("emuleProfile.invalidUserId");
                setIsLoading(false);
                return;
            }

            try {
                console.log("Tentative de récupération des données pour UserId:", userId);
                const userDocRef = doc(db, 'users', userId);
                const userSnap = await getDoc(userDocRef);

                if (userSnap.exists()) {
                    const data = userSnap.data();
                    console.log("Données utilisateur récupérées avec succès:", data);
                    setUserData({
                        ...data,
                        userId: userId,
                        uid: userId // S'assurer que uid est également présent
                    });
                } else {
                    console.error("Aucune donnée trouvée pour l'utilisateur:", userId);
                    setErrorMsg("emuleProfile.profileNotFound");
                }
            } catch (error) {
                console.error("Erreur lors de la récupération du profil:", error);
                setErrorMsg("emuleProfile.loadProfileError");
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserData();
    }, [userId, selectedUser, db]);

    // Calcul des propriétés qui dépendent de userData
    useEffect(() => {
        console.log("EmuleProfil - userData mis à jour:", userData);
    }, [userData]);

    const zodiacImageUrl = mapZodiacValueToImageUrl(userData.zodiacSign);
    const countryName = Country.getCountryByCode(userData.country)?.name || userData.country;
    const allStates = State.getStatesOfCountry(userData.country);
    const stateObj = allStates.find(s => s.isoCode === userData.state);
    const stateName = stateObj
        ? stateObj.name
        : t("emuleProfile.notAvailable");

    const [country, setCountry] = useState('');
    const [state, setState] = useState('');
    const [city, setCity] = useState('');


    const countries = Country.getAllCountries();
    // const states = country ? State.getStatesOfCountry(country.isoCode) : [];
    const states = country ? State.getStatesOfCountry(country) : [];

    const cities = state ? city.getCitiesOfState(country, state) : [];

    const handleCountryChange = (selectedCountryCode) => {
        setCountry(selectedCountryCode);
        setState('');  // Réinitialiser l'état
        setCity('');   // Réinitialiser la ville
    };

    const [showExtraButtons, setShowExtraButtons] = useState(false);

    const toggleExtraButtons = () => {
        setShowExtraButtons(!showExtraButtons);
    };

    const { width, height } = UseWindowSize();

    // usestates pour le chat et la vidéo
    const [showChatWindow, setShowChatWindow] = useState(false);

    let videoOpts;

    if (width <= 320) { // Galaxy S9+
        videoOpts = {
            height: '107px',
            width: '228px',
        };
    } else if (width <= 353) { // Pixel 4 XL
        videoOpts = {
            height: '104.75px',      // Augmenté pour iPhone X
            width: '200.45px',
        };
    } else if (width <= 360) {
        videoOpts = {
            height: '106.75px',      // Augmenté pour iPhone X
            width: '205.25px',
        };
    } else if (width <= 375) { // iPhone X
        videoOpts = {
            height: '108px',      // Augmenté pour iPhone X
            width: '212.5px',
        };
    } else if (width <= 384) { // iPhone X
        videoOpts = {
            height: '110px',      // Augmenté pour iPhone X
            width: '216.75.5px',
        };
    } else if (width <= 390) { // iPhone 12 Pro
        videoOpts = {
            height: '111.5px',
            width: '218.25px',
        };
    } else if (width <= 393) { // iPhone 12 Pro
        videoOpts = {
            height: '112.5px',
            width: '220.25px',
        };
    } else if (width <= 396) { // iPhone 16
        videoOpts = {
            height: '128.5px',
            width: '279px',
        };
    } else if (width <= 412) { // Pixel 7
        videoOpts = {
            height: '132px',
            width: '233px',
        };
    } else if (width <= 414) { // Pixel 7
        videoOpts = {
            height: '132px',
            width: '238px',
        };
    } else if (width <= 430) { // iPhone 14 Pro Max
        videoOpts = {
            height: '130.5px',
            width: '244.75px',
        };
    } else if (width <= 432) { // iPhone 16 Plus
        videoOpts = {
            height: '131.5px',
            width: '245.75px',
        };
    } else if (width <= 480) { // iPhone 16 Plus
        videoOpts = {
            height: '160.5px',
            width: '286.75px',
        };
    } else {
        videoOpts = {
            height: '308.75px',
            width: '765px',
        };
    }

    useEffect(() => {
        let isMounted = true;
        const fetchYouTubeKey = async () => {
            if (userData.favoriteMovie && userData.favoriteMovie.id) {
                try {
                    const videoResponse = await axios.get(
                        `https://api.themoviedb.org/3/movie/${userData.favoriteMovie.id}/videos?api_key=e16900047627f56531a2d3d8d4df9e92&language=fr-FR`
                    );
                    const videoData = videoResponse.data.results.find(
                        (video) => video.site.toLowerCase() === "youtube"
                    );
                    if (videoData && isMounted) {
                        setVideoKey(videoData.key);
                    }
                } catch (error) {
                    console.error("Erreur lors de la récupération de la clé vidéo:", error);
                }
            }
        };

        fetchYouTubeKey();

        // Fonction de nettoyage pour éviter les mises à jour d'état après démontage
        return () => {
            isMounted = false;
        };
    }, [userData.favoriteMovie]);


    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);


    // Gérez l'état de chargement ou d'erreur
    if (isLoading) {
        return (
            <div className="emuleProfil-loading" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 2000
            }}>
                <div style={{ color: 'white', fontSize: '24px' }}>
                    {t("emuleProfile.loadingProfile")}
                </div>
            </div>
        );
    }

    if (errorMsg) {
        return (
            <div className="emuleProfil-error" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 2000
            }}>
                <div style={{
                    color: 'white',
                    backgroundColor: '#500',
                    padding: '20px',
                    borderRadius: '10px',
                    maxWidth: '80%',
                    textAlign: 'center'
                }}>
                    <h3>{t("common.error")}</h3>
                    <p>{t(errorMsg)}</p>
                    <button onClick={onClose} style={{
                        padding: '10px 20px',
                        backgroundColor: '#333',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                    }}>
                        {t("common.close")}
                    </button>
                </div>
            </div>
        );
    }

    // Fonction pour fermer les fenêtres avec nettoyage de ressources
    const handleCloseChat = () => {
        setShowChatWindow(false);
        // Aucune ressource supplémentaire à nettoyer ici, ChatWindow gère son propre nettoyage
    };

    function handleVideoCallClick() {
        try {
            console.log('[EmuleProfil] handleVideoCallClick appelé');
            console.log('[EmuleProfil] userData:', userData);
            console.log('[EmuleProfil] userContext:', userContext);

            if (!userData || !userData.userId) {
                console.error('[EmuleProfil] Données utilisateur manquantes:', userData);
                alert(t("emuleProfile.missingUserData"));
                return;
            }

            setShowVideoCallModal(true);
            console.log('[EmuleProfil] Modal d\'appel vidéo ouverte');
        } catch (error) {
            console.error('[EmuleProfil] Erreur dans handleVideoCallClick:', error);
            alert(
                t("emuleProfile.videoCallError", {
                    message: error.message,
                })
            );
        }
    }

    function handleCloseVideoCall() {
        try {
            console.log('[EmuleProfil] handleCloseVideoCall appelé');
            setShowVideoCallModal(false);
            console.log('[EmuleProfil] Modal d\'appel vidéo fermée');
        } catch (error) {
            console.error('[EmuleProfil] Erreur dans handleCloseVideoCall:', error);
        }
    }

    const profileActivityScore = getUserActivityScore(
        userData,
        userData?.messageCount ?? 0
    );
    const isEmuleMobile = width <= 520;
    const emuleUsername =
        userData?.username || t("emuleProfile.userFallback");
    const publicReviewsTitle = t(
        "movieReviewsCarousel.publicTitle"
    ).replace("{{username}}", emuleUsername);
    const profileUserId = userData?.uid || userData?.userId || userId;
    const normalizedGender = String(userData?.gender || "")
        .trim()
        .toLowerCase();

    const genderKeyMap = {
        male: "male",
        homme: "male",
        female: "female",
        femme: "female",
        nonbinary: "nonBinary",
        "non-binary": "nonBinary",
        autre: "other",
        other: "other",
    };

    const translatedGender = genderKeyMap[normalizedGender]
        ? t(`emuleProfile.genderValues.${genderKeyMap[normalizedGender]}`)
        : userData?.gender || t("emuleProfile.notAvailable");

    const zodiacKey = String(userData?.zodiacSign || "")
        .trim()
        .toLowerCase();

    const translatedZodiac = zodiacKey
        ? t(`zodiac.${zodiacKey}`)
        : t("emuleProfile.notAvailable");

    return (
        <>
            <div className="emuleProfil"
                style={{
                    backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundAttachment: "fixed",
                    backgroundBlendMode: "overlay",
                    backgroundColor: "#200211",
                    opacity: "0.9",
                }}
            >
                <div className="wrapper_Emule container-fluid" >
                    <div className="row rowBanner" id='rowBanner_Emule'>
                        <div className="col-12 col-md-12 colBanner">
                            <div className="banner_EmuleProfil"
                                style={{
                                    backgroundImage: `url(${userData.banner || defaultBannerUrl})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                    margin: "0 0 1,5vh 0",
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    height: '100%',
                                    position: 'relative',
                                    zIndex: "100"

                                }}
                            >
                                <div className="emule-profil-xp-avatar-wrapper">
                                    <UserXpAvatar
                                        avatarUrl={userData.avatar || defaultAvatarUrl}
                                        activityScore={profileActivityScore}
                                        fallbackActivityScore={userData?.messageCount}
                                        showTitle={true}
                                        showActivity={false}
                                        className="user-xp-avatar--emule-profil"
                                        userId={profileUserId}
                                    />

                                    <div
                                        className="div-circularNickname emule-profil-xp-nickname"
                                        id="div-circularNickname_Emule"
                                    >
                                        {isEmuleMobile ? (
                                            <span className="emule-profil-mobile-nickname-text">
                                                {emuleUsername}
                                            </span>
                                        ) : (
                                            <CircularNicknameSvg username={emuleUsername} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row rowBio_Emule" id='rowBio_EmuleMt'>
                        <div className="col-12 col-md-12">
                            <div className="div-biseau" id='div-biseau_EmuleMt'>
                                <h2 className='titreMabio'>
                                    {t("emuleProfile.biographyTitle")}
                                </h2>
                                <span className='spanBio'>
                                    <blockquote className="citationProfil" id='citationProfil_Emule'>
                                        <q id='spêcial_Bq_Quote'>{userData.quote}</q>
                                    </blockquote>
                                    <div className='bioText' dangerouslySetInnerHTML={{ __html: userData.biography }} id='bioTextEmuleId'></div>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="row rowIdentify" id="rowIdentify_Emule">
                        <div className="col-12 col-md-12">
                            <div
                                className="identification1"
                                id="identification1_Emule"
                            >
                                <h2 className="infoPersos">
                                    {t("emuleProfile.personalInfoTitle")}
                                </h2>

                                <div
                                    className="divGender"
                                    id="divGender_Emule"
                                >
                                    <span>
                                        {t("emuleProfile.gender")}&nbsp;
                                    </span>

                                    <span className="inputGender">
                                        {translatedGender}
                                    </span>
                                </div>

                                <div
                                    className="divAge"
                                    id="divAge_Emule"
                                >
                                    <span>
                                        {t("emuleProfile.age")}&nbsp;
                                    </span>

                                    <span className="inputAge">
                                        {userData.age ||
                                            t("emuleProfile.notAvailable")}
                                    </span>
                                </div>

                                <div
                                    className="inputZodiacSignBase"
                                    id="inputZodiacSignBase_Emule"
                                >
                                    <span>
                                        {t("emuleProfile.zodiacSign")}&nbsp;
                                    </span>

                                    <figure className="inputZodiacSign">
                                        <img
                                            src={zodiacImageUrl}
                                            alt={translatedZodiac}
                                            id="imgZodiacSignId"
                                        />

                                        <figcaption>
                                            {translatedZodiac}
                                        </figcaption>
                                    </figure>
                                </div>

                                <div
                                    className="divOccupation"
                                    id="divOccupation_Emule"
                                >
                                    <span>
                                        {t("emuleProfile.occupation")}&nbsp;
                                    </span>

                                    <span className="inputOccupation">
                                        {userData.occupation ||
                                            t("emuleProfile.notAvailable")}
                                    </span>
                                </div>

                                <div
                                    className="divPays"
                                    id="divPays_Emule"
                                >
                                    <span>
                                        {t("emuleProfile.country")}&nbsp;
                                    </span>

                                    <span className="inputPays">
                                        {countryName ||
                                            t("emuleProfile.notAvailable")}
                                    </span>
                                </div>

                                <div
                                    className="divEtatProvince"
                                    id="divEtatProvince_Emule"
                                >
                                    <span>
                                        {t("emuleProfile.stateProvince")}&nbsp;
                                    </span>

                                    <span className="inputEtatProvince">
                                        {String(stateName)}
                                    </span>
                                </div>

                                <div
                                    className="divVille"
                                    id="divVille_Emule"
                                >
                                    <span>
                                        {t("emuleProfile.city")}&nbsp;
                                    </span>

                                    <span className="inputVille">
                                        {userData.city ||
                                            t("emuleProfile.notAvailable")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="row rowIdentify"
                        id="rowCinephileProfile_Emule"
                    >
                        <div className="col-12 col-md-12">
                            <div className="identification2">
                                <h2
                                    className="infoIdentify"
                                    id="infoIdentifyEmule"
                                >
                                    {t("emuleProfile.cinephileProfileTitle")}
                                </h2>

                                <div className="preferContainer">
                                    <h4
                                        className="h4Prefer_EmuleProfil"
                                        id="h4FavoriteMovies_Emule"
                                    >
                                        {t("emuleProfile.favoriteMovies")}
                                    </h4>

                                    <div className="rowPrefer rowpreferCategories">
                                        {userData.favoritePreviews &&
                                            userData.favoritePreviews.map((movie) => (
                                                <Link
                                                    to={`/ficheFilm/${movie.id}`}
                                                    key={movie.id}
                                                >
                                                    <PreviewMyFavoritesMovies
                                                        movie={movie}
                                                        className="previewEmule"
                                                    />
                                                </Link>
                                            ))}
                                    </div>

                                    <h4
                                        className="h4Prefer_EmuleProfil"
                                        id="h4FavoriteActors_Emule"
                                    >
                                        {t("emuleProfile.favoriteActors")}
                                    </h4>

                                    <div className="rowPrefer rowpreferCategories">
                                        {userData.favoriteActors &&
                                            userData.favoriteActors.map((actor) => (
                                                <Link
                                                    to={`/actorDetails/${actor.id}`}
                                                    key={actor.id}
                                                >
                                                    <MyFavoritesActors actor={actor} />
                                                </Link>
                                            ))}
                                    </div>

                                    <h4
                                        className="h4Prefer_EmuleProfil"
                                        id="h4FavoriteGenres_Emule"
                                    >
                                        {t("emuleProfile.favoriteGenres")}
                                    </h4>

                                    <div className="rowPrefer rowpreferCategories">
                                        {userData.favoriteCategories &&
                                            userData.favoriteCategories.map((category) => (
                                                <My3FavoriteCategories
                                                    key={category.id}
                                                    category={category}
                                                />
                                            ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* CARROUSEL DES AVIS MOVIEFRENZY DU PROFIL VISITÉ */}
                    <div
                        className="row rowReviewsCarousel rowReviewsCarousel--emule"
                        id="rowReviewsCarousel_Emule"
                    >
                        <div className="col-12">
                            <MovieReviewsCarousel
                                userId={profileUserId}
                                title={publicReviewsTitle}
                                autoScroll={true}
                            />
                        </div>
                    </div>
                    <div className="row rowFavoritePreview" id='rowFavoritePreview'>
                        <div className="col-12 col-md-6">
                            <h3 className="h3CooupCoeur">
                                {t("emuleProfile.favoritePreview")}
                            </h3>
                            <div className="aspect-ratio-container">
                                {videoKey ? (
                                    <YouTube
                                        videoId={videoKey}
                                        className="iFrameStyle_ForEmuleProfil"
                                        id="iFrameStyleIdTestEmule"
                                        opts={videoOpts}
                                        ref={youtubePlayerRef}
                                        // style={{opacity: '0', outline: '1px solid RED', zIndex: zIndex}}
                                        onMouseEnter={() => {
                                            setIsHovered(true);
                                            setZIndex(1000);
                                        }}
                                        onMouseLeave={() => {
                                            setIsHovered(false);
                                            setZIndex(40000);
                                        }}
                                        onReady={(event) => {
                                            event.target.pauseVideo();
                                            event.target.setVolume(0);
                                        }}
                                    />
                                ) : (
                                    <p>
                                        {t("emuleProfile.noFavoritePreview")}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row rowEditionProfil" id='rowEditionProfil_Emule'>
                        <div className="col-12 col-md-6">
                            <div className="divCircularEditionProfil" id='divCircularEditionProfil_Emule'>
                                <Canvas
                                    gl={{
                                        outputColorSpace: SRGBColorSpace,
                                        toneMapping: ACESFilmicToneMapping,
                                        toneMappingExposure: 1.0,
                                    }}
                                >
                                    <ambientLight intensity={0.95} />
                                    <Suspense fallback={null}>
                                        <ThreeModel />
                                    </Suspense>
                                    <OrbitControls
                                        enableZoom={true}
                                        zoomSpeed={0.6}
                                        minDistance={5}
                                        maxDistance={20}
                                    />
                                </Canvas>
                            </div>
                            <div className="blocMessageInterUser" id="blocMessageInterUser_Emule">
                                {/* ← Bouton principal : wrapperButton_Emule d'origine, avec ta SCSS */}
                                <div
                                    className="wrapperButton"
                                    id="wrapperButton_Emule"
                                    onClick={toggleExtraButtons}
                                >
                                    <input type="checkbox" />
                                    <label>
                                        <i className="icon-off">
                                            {t("emuleProfile.message")}
                                        </i>
                                    </label>
                                </div>

                                {showExtraButtons && (
                                    <>
                                        {/* ← Bouton texte 1‑à‑1 */}
                                        <div
                                            className="wrapperMessageButton"
                                            id="wrapperMessageButton_Emule"
                                            onClick={() => setShowChatWindow(true)}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/messageUserIcon.png`}
                                                alt={t("emuleProfile.messageIconAlt")}
                                            />
                                        </div>

                                        {/* ← Bouton appel vidéo */}
                                        <div
                                            className="wrapperVidMessageButton"
                                            id="wrapperVideoCallButton_Emule"
                                            // style={{
                                            //     padding: '10px',
                                            //     backgroundColor: '#333',
                                            //     borderRadius: '50%',
                                            //     textAlign: 'center',
                                            //     fontSize: '0.8rem',
                                            //     cursor: 'pointer',
                                            //     display: 'flex',
                                            //     alignItems: 'center',
                                            //     justifyContent: 'center',
                                            //     marginLeft: '10px'
                                            // }}
                                            title={t("emuleProfile.videoCallSoon")}
                                            onClick={handleVideoCallClick}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/messageVidUserIcon.png`}
                                                alt={t("emuleProfile.videoCallAlt")}
                                            // style={{ width: '40px', height: '40px' }}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showChatWindow && (
                <ChatWindow
                    currentUserId={userContext.userId}
                    peerUser={userData}
                    onClose={handleCloseChat}
                />
            )}
            {showVideoCallModal && (
                <VideoCallManager
                    isOpen={showVideoCallModal}
                    onClose={handleCloseVideoCall}
                    targetUser={userData}
                    mode="outgoing"
                />
            )}
        </>
    );
};  // Fin de EmuleProfil

export default EmuleProfil;
